import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { Globe, Menu, X, MessageCircle, Phone, Mail, Instagram, ArrowRight, MapPin, Clock, ChevronDown, Truck, Send, CheckCircle, Zap, Calendar } from "lucide-react";

const navLabels = {
  en: { home: "Home", about: "About", services: "Services", "service-areas": "Service Areas", faq: "FAQ", "rate-us": "Rate Us", contact: "Contact" },
  ar: { home: "الرئيسية", about: "عن الشركة", services: "الخدمات", "service-areas": "مناطق الخدمة", faq: "الأسئلة الشائعة", "rate-us": "قيّمنا", contact: "اتصل بنا" }
};

const navSections = ["home", "about", "services", "service-areas", "faq", "rate-us", "contact"] as const;

const serviceRoutes = [
  "/road-transportation", "/re-export", "/customs-clearance", "/cross-border", "/import-export-documentation"
];

const sectionPaths = ["/about", "/services", "/service-areas", "/faq", "/rate-us", "/contact"];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { lang, toggleLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "";
  const isHomeOrSection = isHome || sectionPaths.includes(location.pathname);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const activeSectionRef = useRef("home");
  const ticking = useRef(false);

  useEffect(() => {
    const pathSection = sectionPaths.includes(location.pathname) ? location.pathname.replace("/", "") : "";
    const params = new URLSearchParams(location.search);
    const section = params.get("section");
    const hash = location.hash.replace("#", "");
    const target = pathSection || section || hash;
    if (target && document.getElementById(target)) {
      if (hash || section) {
        navigate(`/${hash || section}`, { replace: true });
      } else {
        setActiveSection(target);
        activeSectionRef.current = target;
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (!isHomeOrSection || ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const sections = ["home", "about", "services", "service-areas", "faq", "rate-us", "contact"];
        const scrollPosition = window.scrollY + 120;
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetHeight = element.offsetHeight;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              if (activeSectionRef.current !== section) {
                activeSectionRef.current = section;
                setActiveSection(section);
                const newPath = section === "home" ? "/" : `/${section}`;
                history.replaceState(null, "", newPath);
              }
              break;
            }
          }
        }
        ticking.current = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomeOrSection]);

  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    if (isHomeOrSection) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      history.replaceState(null, "", id === "home" ? "/" : `/${id}`);
    } else {
      navigate(`/${id}`);
    }
  };

  const handleQuoteClick = () => {
    setIsMenuOpen(false);
    if (isHomeOrSection) {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      history.replaceState(null, "", "/contact");
    } else {
      navigate("/contact");
    }
  };

  const isServicePage = serviceRoutes.includes(location.pathname);

  return (
    <div className={`min-h-screen bg-white ${lang === "ar" ? "text-right" : ""}`}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100" : "bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <a href="/" className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg border-2 border-white/20 flex-shrink-0">
                  <img src="/logo.png" alt={t("Abu Ali Logistics Services Logo", "شعار أبو علي اللوجستية")} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h1 className={`text-xl font-bold transition-colors ${isScrolled ? "text-gray-900" : "text-white"}`}>Abu Ali Logistics</h1>
                  <p className={`text-sm hidden sm:block transition-colors ${isScrolled ? "text-gray-600" : "text-white/80"}`}>خدمات ابوعلي اللوجستية</p>
                </div>
              </a>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6 rtl:space-x-reverse">
              {navSections.map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`font-medium relative transition-colors ${
                    isScrolled ? "text-gray-700 hover:text-blue-700" : "text-white/90 hover:text-white"
                  } ${activeSection === section && isHomeOrSection ? (isScrolled ? "text-blue-700" : "text-white") : ""}`}
                >
                  {navLabels[lang][section]}
                  {activeSection === section && isHomeOrSection && <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-orange-500 rounded-full"></div>}
                </button>
              ))}
              <button
                onClick={toggleLang}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-300 ${
                  isScrolled
                    ? "border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                    : "border-white/30 text-white/90 hover:border-white/60 hover:text-white"
                }`}
                aria-label={t("Switch to Arabic", "التبديل إلى الإنجليزية")}
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{lang === "en" ? "عربي" : "English"}</span>
              </button>
              <button
                onClick={handleQuoteClick}
                className="border-2 border-white/30 text-white px-5 py-2 rounded-lg font-medium hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
              >
                {t("Request Quote", "طلب عرض سعر")}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={toggleLang}
                className={`p-2 rounded-lg transition-colors text-xs font-medium border ${
                  isScrolled ? "border-gray-300 text-gray-700" : "border-white/30 text-white/90"
                }`}
                aria-label={t("Switch to Arabic", "التبديل إلى الإنجليزية")}
              >
                <Globe className="w-4 h-4" />
                <span className="text-[10px]">{lang === "en" ? "عربي" : "English"}</span>
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${isScrolled ? "hover:bg-gray-100" : "hover:bg-white/10"}`}
                aria-label={t("Toggle menu", "فتح/إغلاق القائمة")}
              >
                {isMenuOpen ? (
                  <X className={`w-6 h-6 ${isScrolled ? "text-gray-900" : "text-white"}`} />
                ) : (
                  <Menu className={`w-6 h-6 ${isScrolled ? "text-gray-900" : "text-white"}`} />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg border-t border-gray-100">
              <div className="px-4 py-6 space-y-4">
                {navSections.map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className="block w-full text-left rtl:text-right text-gray-700 hover:text-blue-700 transition-colors font-medium"
                  >
                    {navLabels[lang][section]}
                  </button>
                ))}
                <button
                  onClick={handleQuoteClick}
                  className="w-full bg-gradient-to-r from-blue-700 to-blue-800 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-medium"
                >
                  {t("Request Quote", "طلب عرض سعر")}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Page content */}
      {children}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className={`flex items-center ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-3"} mb-4`}>
                <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg flex-shrink-0">
                  <img src="/logo.png" alt={t("Abu Ali Logistics Services Logo", "شعار أبو علي اللوجستية")} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">ABU ALI LOGISTICS SERVICES</h3>
                  <p className="text-gray-400 text-sm">{t("ABU ALI LOGISTICS SERVICES", "خدمات ابوعلي اللوجستية")}</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                {t(
                  "Your trusted partner for road transportation and logistics services across the GCC. Specializing in GCC road freight, re-export services, customs clearance, cross border shipping, and import/export documentation. Registered as an Individual Establishment in Bahrain (CR: 175553-1).",
                  "شريكك الموثوق لخدمات النقل البري والخدمات اللوجستية في جميع أنحاء دول مجلس التعاون الخليجي. متخصصون في الشحن البري لدول الخليج، وخدمات إعادة التصدير، والتخليص الجمركي، والشحن عبر الحدود، ومستندات الاستيراد والتصدير. مسجلة كمؤسسة فردية في البحرين (السجل التجاري: 175553-1)."
                )}
              </p>
              <div className={`flex ${lang === "ar" ? "flex-row-reverse" : ""} space-x-3 rtl:space-x-reverse`}>
                <a href="https://www.instagram.com/abuali_logistics/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors" aria-label={t("Follow us on Instagram", "تابعنا على انستغرام")}>
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.tiktok.com/@abuali_90a" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors" aria-label={t("Follow us on TikTok", "تابعنا على تيك توك")}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </a>
                <a href="mailto:info@abualilogistics.com" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors" aria-label={t("Send us an email", "أرسل لنا بريداً إلكترونياً")}>
                  <Mail className="w-5 h-5" />
                </a>
                <a href="tel:+97334449381" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-green-600 transition-colors" aria-label={t("Call us", "اتصل بنا")}>
                  <Phone className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">{t("Services", "الخدمات")}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => { if (isHomeOrSection) { document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); } else { navigate("/services"); } }} className="hover:text-white transition-colors">{t("Road Transportation", "النقل البري")}</button></li>
                <li><button onClick={() => { if (isHomeOrSection) { document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); } else { navigate("/services"); } }} className="hover:text-white transition-colors">{t("Re-Export Services", "خدمات إعادة التصدير")}</button></li>
                <li><button onClick={() => { if (isHomeOrSection) { document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); } else { navigate("/services"); } }} className="hover:text-white transition-colors">{t("Customs Clearance", "التخليص الجمركي")}</button></li>
                <li><button onClick={() => { if (isHomeOrSection) { document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); } else { navigate("/services"); } }} className="hover:text-white transition-colors">{t("Cross Border Shipping", "الشحن عبر الحدود")}</button></li>
                <li><button onClick={() => { if (isHomeOrSection) { document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); } else { navigate("/services"); } }} className="hover:text-white transition-colors">{t("Import & Export Documentation", "مستندات الاستيراد والتصدير")}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">{t("Contact", "الاتصال")}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="tel:+97334449381" className="hover:text-white transition-colors">{lang === "en" ? "+973 3444 9381" : <span dir="ltr">+973 3444 9381</span>}</a></li>
                <li><a href="https://wa.me/97334449381" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t("WhatsApp", "واتساب")}</a></li>
                <li><a href="mailto:info@abualilogistics.com" className="hover:text-white transition-colors">info@abualilogistics.com</a></li>
                <li><a href="https://www.instagram.com/abuali_logistics/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{lang === "en" ? "@abuali_logistics" : "\u200E@abuali_logistics\u200E"}</a></li>
                <li><a href="https://www.tiktok.com/@abuali_90a" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{lang === "en" ? "@abuali_90a" : "\u200E@abuali_90a\u200E"}</a></li>
                <li className="hover:text-white transition-colors">A'ALI/عالي, {t("Bahrain", "البحرين")}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">© 2024 {t("ABU ALI LOGISTICS SERVICES (خدمات ابوعلي اللوجستية) — Individual Establishment. All rights reserved.", "ABU ALI LOGISTICS SERVICES (خدمات ابوعلي اللوجستية) — مؤسسة فردية. جميع الحقوق محفوظة.")}</p>
              <div className={`flex flex-col md:flex-row items-center space-y-2 md:space-y-0 ${lang === "ar" ? "md:space-x-reverse" : "md:space-x-6"} text-sm text-gray-400`}>
                <span>{t("CR: 175553-1 | Individual Establishment — Bahrain", "السجل التجاري: 175553-1 | مؤسسة فردية — البحرين")}</span>
                <span>ISIC: 5229091</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <div className="fixed right-6 rtl:right-auto rtl:left-6 z-40 flex flex-col space-y-3 rtl:space-x-reverse bottom-6">
        <a href="https://wa.me/97334449381" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all duration-300" aria-label={t("Contact us on WhatsApp", "تواصل معنا على واتساب")}>
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
        <a href="tel:+97334449381" className="w-14 h-14 bg-blue-700 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-800 hover:scale-110 transition-all duration-300" aria-label={t("Call us", "اتصل بنا")}>
          <Phone className="w-7 h-7 text-white" />
        </a>
      </div>


    </div>
  );
}
